'use client';

import { useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import type { Message } from '@repo/api';
import { CONTENT_TYPES, MESSAGE_TYPES } from '@repo/utils';
import { ChatMessage } from './ChatMessage';
import { MessageBubble } from './MessageBubble';
import { BeatLoader } from '@/components/ui';
import { useIsMobile } from '@/hooks/useDeviceType';
import { useAnalytics } from '@/hooks/useAnalytics';

interface ChatMessageListProps {
  messages: Message[];
  isInitializing: boolean;
  isPolling: boolean;
  fetchPaginatedChat: boolean;
  totalMessageCount: number;
  shouldAutoScroll: boolean;
  setShouldAutoScroll: (v: boolean) => void;
  userSentMessageRef: React.MutableRefObject<boolean>;
  onLoadMore: () => void;
  onSlotSelect: (value: string, message: Message) => void;
  onRetry: (message: Message) => void;
  onUrlClick?: (url: string) => void;
  className?: string;
  isOverlay?: boolean;
}

export function ChatMessageList({
  messages,
  isInitializing,
  isPolling,
  fetchPaginatedChat,
  totalMessageCount,
  shouldAutoScroll,
  setShouldAutoScroll,
  userSentMessageRef,
  onLoadMore,
  onSlotSelect,
  onRetry,
  onUrlClick,
  className,
  isOverlay,
}: ChatMessageListProps) {
  const isMobile = useIsMobile();
  const analytics = useAnalytics();

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesContentRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const didInitialScrollRef = useRef(false);
  const lastScrollAtRef = useRef(0);
  const isPaginatingRef = useRef(false);
  const prevScrollHeightRef = useRef(0);
  const wasPollingRef = useRef(false);

  const scrollToBottom = useCallback((behavior: ScrollBehavior) => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const now = Date.now();
    if (now - lastScrollAtRef.current < 50) return;
    lastScrollAtRef.current = now;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        try {
          container.scrollTo({ top: container.scrollHeight, behavior });
        } catch {
          container.scrollTop = container.scrollHeight;
        }
      });
    });
  }, []);

  // Initial scroll to bottom once messages load.
  // Scroll synchronously inside useLayoutEffect (before browser paint) so there's
  // no visible flash of messages at the top when the loading overlay disappears.
  useLayoutEffect(() => {
    if (!messagesContainerRef.current) return;
    if (isInitializing) return; // wait until data for this chat has loaded
    if (messages.length === 0) return;
    if (didInitialScrollRef.current) return;
    didInitialScrollRef.current = true;
    setShouldAutoScroll(true);
    messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
  }, [isInitializing, messages.length, setShouldAutoScroll]);

  // ResizeObserver: stay pinned when content height changes (images/cards loading)
  useEffect(() => {
    const el = messagesContentRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (!shouldAutoScroll || isPaginatingRef.current) return;
      scrollToBottom('auto');
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [shouldAutoScroll, scrollToBottom]);

  // Scroll when bot starts replying
  useEffect(() => {
    const botStartedReplying = isPolling && !wasPollingRef.current;
    if (botStartedReplying) {
      setShouldAutoScroll(true);
      scrollToBottom('auto');
    }
    wasPollingRef.current = isPolling;
  }, [isPolling, scrollToBottom, setShouldAutoScroll]);

  // Scroll after user sends (triggered by userSentMessageRef)
  useEffect(() => {
    if (!userSentMessageRef.current) return;
    scrollToBottom('smooth');
  }, [messages.length, userSentMessageRef, scrollToBottom]);

  // Keep pinned after keyboard closes on mobile
  useEffect(() => {
    if (!isMobile || typeof window === 'undefined') return;
    const onKeyboardChange = (e: Event) => {
      const detail = (e as CustomEvent<{ isOpen: boolean }>).detail;
      if (!detail || detail.isOpen || !shouldAutoScroll) return;
      scrollToBottom('auto');
    };
    window.addEventListener('keyboardchange', onKeyboardChange as EventListener);
    return () => window.removeEventListener('keyboardchange', onKeyboardChange as EventListener);
  }, [isMobile, shouldAutoScroll, scrollToBottom]);

  // Maintain scroll position after paginating
  useLayoutEffect(() => {
    if (isPaginatingRef.current && messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      const delta = container.scrollHeight - prevScrollHeightRef.current;
      container.scrollTop = container.scrollTop + delta;
      isPaginatingRef.current = false;
    }
  }, [messages.length]);

  return (
    <div
      ref={messagesContainerRef}
      className={`relative flex-1 flex flex-col overflow-y-auto overflow-x-hidden px-6 py-4 min-h-0 bg-primary touch-pan-y overscroll-contain ${className ?? ''}`}
      style={{ WebkitOverflowScrolling: 'touch', maxWidth: '100%' }}
      onScroll={e => {
        const target = e.currentTarget;
        const { scrollTop, scrollHeight, clientHeight } = target;

        // Load more when near top
        if (scrollTop < 100 && !fetchPaginatedChat && totalMessageCount > messages.length) {
          prevScrollHeightRef.current = scrollHeight;
          isPaginatingRef.current = true;
          onLoadMore();
        }

        // Auto-scroll gate
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
        if (!isNearBottom) {
          setShouldAutoScroll(false);
        } else if (isNearBottom && !userSentMessageRef.current) {
          setShouldAutoScroll(true);
        }
      }}
    >
      {/* Initializing overlay */}
      {isInitializing && (
        <div className="absolute inset-0 z-50 flex min-h-[240px] w-full items-center justify-center bg-primary">
          <BeatLoader />
        </div>
      )}

      <div ref={messagesContentRef} className="flex min-h-full flex-col">
        {/* Empty state */}
        {messages.length === 0 && !isInitializing && (
          <div className="flex items-center justify-center h-full text-secondary text-body-2">
            No messages yet. Start the conversation!
          </div>
        )}

        {/* Pagination spinner */}
        {fetchPaginatedChat && (
          <div className="flex items-center justify-center py-4">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Messages */}
        {!isInitializing &&
          messages.map((message, index) => {
            const prev = index > 0 ? messages[index - 1] : null;
            const isGrouped =
              !!prev &&
              prev.contentType === CONTENT_TYPES.TEXT &&
              prev.senderType === message.senderType &&
              new Date(message.atCreated).getTime() - new Date(prev.atCreated).getTime() <= 60_000;

            return (
              <div key={message.id} data-message-id={message.id}>
                <ChatMessage
                  message={message}
                  isGrouped={isGrouped}
                  isFirst={index === 0}
                  isOverlay={isOverlay ?? false}
                  onSlotSelect={onSlotSelect}
                  onRetry={onRetry}
                  onUrlClick={url => {
                    if (url.includes('buildingReport=true')) {
                      analytics.track('view_report_link', { screen: 'message' });
                    }
                    if (url.includes('buildingReviews=true')) {
                      analytics.track('view_reviews_link', { screen: 'message' });
                    }
                    onUrlClick?.(url);
                  }}
                />
              </div>
            );
          })}

        {/* Typing indicator */}
        {isPolling && !isInitializing && (
          <div className="mt-2">
            <MessageBubble
              message={{
                id: 'loading-indicator',
                senderType: MESSAGE_TYPES.BOT,
                contentType: CONTENT_TYPES.LOADING,
                contentMessage: '',
                atCreated: new Date().toISOString(),
              }}
            />
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>
    </div>
  );
}
